import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Briefcase, 
  Code, 
  Users, 
  Award, 
  LayoutDashboard, 
  BarChart, 
  Clock, 
  CheckCircle,
  ArrowRight,
  Terminal,
  Cpu,
  Monitor,
  Megaphone,
  Database,
  Send,
  Palette,
  Smartphone,
  ShieldCheck,
  Search,
  Bot,
  XCircle
} from "lucide-react";
import FooterNew from "../components/FooterNew";
import { getBackendUrl } from "../utils/auth";

const Internships = () => {
  const API_URL = getBackendUrl();
  const [activePrograms, setActivePrograms] = useState([]);

  useEffect(() => {
    const fetchActivePrograms = async () => {
      try {
        const response = await fetch(`${API_URL}/api/internship/programs`);
        const data = await response.json();
        if (data.success && Array.isArray(data.programs)) {
          setActivePrograms(data.programs);
        }
      } catch (error) {
        console.error("Error fetching active programs:", error);
      }
    };
    fetchActivePrograms();
  }, [API_URL]);

  const getProgramIcon = (iconName) => {
    const iconProps = { className: "w-8 h-8 text-sky-400" };
    switch (iconName) {
      case 'Monitor': return <Monitor {...iconProps} />;
      case 'Database': return <Database {...iconProps} />;
      case 'Bot': return <Bot {...iconProps} />;
      case 'Terminal': return <Terminal {...iconProps} />;
      case 'Megaphone': return <Megaphone {...iconProps} />;
      case 'Palette': return <Palette {...iconProps} />;
      case 'Smartphone': return <Smartphone {...iconProps} />;
      default: return <Briefcase {...iconProps} />;
    }
  };

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    college: "",
    domain: "",
    objective: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: "", text: "" });

  // Certificate Verification State
  const [verifyCertNo, setVerifyCertNo] = useState("");
  const [verifyStatus, setVerifyStatus] = useState("idle"); // idle, loading, success, error
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifyError, setVerifyError] = useState("");

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!verifyCertNo.trim()) return;
    
    setVerifyStatus("loading");
    setVerifyResult(null);
    setVerifyError("");

    try {
      const response = await fetch(`${API_URL}/api/certificates/verify/${verifyCertNo}`);
      const data = await response.json();

      if (data.success) {
        setVerifyResult(data.certificate);
        setVerifyStatus("success");
      } else {
        setVerifyError(data.message || "Invalid Certificate");
        setVerifyStatus("error");
      }
    } catch (error) {
      setVerifyError("Server error. Please try again later.");
      setVerifyStatus("error");
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleApplyClick = (domainTitle) => {
    setFormData({ ...formData, domain: domainTitle });
    document.getElementById("apply-form")?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage({ type: "", text: "" });
    
    try {
      const response = await fetch(`${API_URL}/api/internship/apply`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        throw new Error("Failed to submit");
      }
      
      setSubmitMessage({ type: "success", text: "Application submitted successfully! Our team will contact you soon." });
      setFormData({ name: "", email: "", phone: "", college: "", domain: "", objective: "" });
    } catch (error) {
      setSubmitMessage({ type: "error", text: "Something went wrong. Please try again later." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const courseDomains = [
    {
      title: "Frontend Development",
      duration: "3 Months",
      modules: [
        "HTML5, CSS3 & Responsive Design",
        "JavaScript (ES6+) & DOM Manipulation",
        "React.js & State Management (Redux)",
        "Modern Styling with Tailwind CSS",
        "Next.js Basics & Deployment"
      ],
      icon: <Monitor className="w-8 h-8 text-sky-400" />
    },
    {
      title: "Backend Development",
      duration: "3 Months",
      modules: [
        "Node.js Runtime & Architecture",
        "Express.js Framework & Routing",
        "RESTful API Design & Authentication",
        "MongoDB & Mongoose (NoSQL)",
        "PostgreSQL Basics (SQL)"
      ],
      icon: <Database className="w-8 h-8 text-blue-500" />
    },
    {
      title: "Artificial Intelligence & Machine Learning (AI/ML)",
      duration: "4 Months",
      modules: [
        "Data Preprocessing & EDA",
        "Supervised & Unsupervised Learning",
        "Deep Learning & Neural Networks",
        "NLP & Computer Vision Basics",
        "Model Deployment & MLOps"
      ],
      icon: <Bot className="w-8 h-8 text-sky-400" />
    },
    {
      title: "Python Development",
      duration: "2 Months",
      modules: [
        "Python Fundamentals & OOPs",
        "Data Structures & Algorithms",
        "Web Scraping & Automation",
        "Flask/Django Backend Basics",
        "Working with APIs & Databases"
      ],
      icon: <Terminal className="w-8 h-8 text-blue-500" />
    },
    {
      title: "Digital Marketing",
      duration: "2 Months",
      modules: [
        "SEO (Search Engine Optimization)",
        "Social Media Marketing (SMM)",
        "Google Ads & PPC Campaigns",
        "Content Marketing Strategy",
        "Web Analytics & Performance Tracking"
      ],
      icon: <Megaphone className="w-8 h-8 text-blue-500" />
    },
    {
      title: "UI/UX Design",
      duration: "2 Months",
      modules: [
        "Design Thinking & User Research",
        "Wireframing & Information Architecture",
        "High-Fidelity Prototyping in Figma",
        "Color Theory & Typography",
        "Usability Testing & Handoff"
      ],
      icon: <Palette className="w-8 h-8 text-sky-400" />
    },
    {
      title: "App Development",
      duration: "4 Months",
      modules: [
        "JavaScript/TypeScript for Mobile",
        "React Native Core Components & APIs",
        "Navigation (React Navigation)",
        "State Management (Redux/Context)",
        "App Store & Play Store Deployment"
      ],
      icon: <Smartphone className="w-8 h-8 text-blue-500" />
    }
  ];

  const displayPrograms = activePrograms.length > 0 
    ? activePrograms.map(p => ({
        title: p.title,
        duration: p.duration,
        modules: p.modules,
        icon: getProgramIcon(p.iconName)
      }))
    : courseDomains;

  const features = [
    {
      title: "Live Industry Projects",
      desc: "Work on production-ready applications instead of dummy assignments to build a strong portfolio.",
      icon: <Code className="w-6 h-6 text-ai-glow" />,
    },
    {
      title: "Dedicated Mentorship",
      desc: "Get 1-on-1 personalized guidance and doubt-solving sessions from industry experts.",
      icon: <Users className="w-6 h-6 text-accent-light" />,
    },
    {
      title: "Verified Certification",
      desc: "Receive a recognized Training & Internship Certificate along with a strong Letter of Recommendation (LOR).",
      icon: <Award className="w-6 h-6 text-neon-pink" />,
    },
    {
      title: "Personalized Dashboard",
      desc: "Track your progress, view mentor feedback, and manage your tasks seamlessly.",
      icon: <LayoutDashboard className="w-6 h-6 text-ai-glow" />,
    },
    {
      title: "Skill Assessment",
      desc: "Regular performance reports to help you identify areas for improvement and track your growth.",
      icon: <BarChart className="w-6 h-6 text-accent-light" />,
    },
    {
      title: "Flexible Timings",
      desc: "Self-paced modules combined with scheduled live sessions. Missed a session? Watch the recording anytime.",
      icon: <Clock className="w-6 h-6 text-ai-glow" />,
    },
  ];

  const journeySteps = [
    {
      step: "01",
      title: "Apply & Get Selected",
      desc: "Submit your application. We select candidates based on their enthusiasm to learn and build.",
      icon: <Briefcase className="w-5 h-5 text-ai-glow" />
    },
    {
      step: "02",
      title: "Learn & Build",
      desc: "Engage in hands-on training to master the required tech stack and build mini-projects.",
      icon: <Cpu className="w-5 h-5 text-accent-light" />
    },
    {
      step: "03",
      title: "Live Project Development",
      desc: "Work in a team to build a real-world, industry-standard project from scratch.",
      icon: <Terminal className="w-5 h-5 text-neon-pink" />
    },
    {
      step: "04",
      title: "Graduate & Get Placed",
      desc: "Complete the program, receive your certification, and get ready for placement assistance.",
      icon: <Award className="w-5 h-5 text-ai-glow" />
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <div className="min-h-screen pt-24 pb-0 overflow-hidden relative">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[60%] h-[60%] bg-accent/30 rounded-full blur-[150px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[60%] h-[60%] bg-secondary/20 rounded-full blur-[150px]" />
        <div className="absolute top-[40%] right-[20%] w-[30%] h-[30%] bg-accent-light/15 rounded-full blur-[120px]" />
        <div className="absolute inset-0 tech-grid-dense opacity-40" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Certificate Verification Section */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-3xl mx-auto mb-16 frosted-surface rounded-2xl p-6 border border-sky-400/20 shadow-[0_0_30px_rgba(56,189,248,0.1)]"
        >
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-1 w-full">
              <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                <ShieldCheck className="w-6 h-6 text-sky-400" />
                Verify Certificate
              </h3>
              <p className="text-sm text-slate-400 mb-4">
                Enter your CCAI Certificate Number to verify its authenticity.
              </p>
              
              <form onSubmit={handleVerify} className="relative w-full">
                <input 
                  type="text" 
                  value={verifyCertNo}
                  onChange={(e) => setVerifyCertNo(e.target.value)}
                  placeholder="e.g. CCAI-INT-2026-ABCD"
                  className="w-full bg-slate-900/50 border border-slate-700 rounded-lg py-3 pl-4 pr-32 text-white focus:outline-none focus:border-sky-400 transition-colors uppercase font-mono"
                />
                <button 
                  type="submit"
                  disabled={verifyStatus === 'loading'}
                  className="absolute right-1 top-1 bottom-1 bg-sky-500 hover:bg-sky-400 text-slate-900 font-bold px-4 rounded-md transition-colors flex items-center gap-2 disabled:opacity-70"
                >
                  {verifyStatus === 'loading' ? <div className="w-5 h-5 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /> : <><Search className="w-4 h-4" /> Verify</>}
                </button>
              </form>
            </div>
          </div>

          {verifyStatus === 'success' && verifyResult && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-6 p-4 rounded-xl bg-green-500/10 border border-green-500/20"
            >
              <div className="flex items-start gap-4">
                <div className="bg-green-500/20 p-2 rounded-full">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <h4 className="text-lg font-bold text-green-400 mb-1">Authentic Certificate</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm mt-3">
                    <div>
                      <span className="text-slate-400 block text-xs uppercase tracking-wider">Student Name</span>
                      <span className="text-white font-medium">{verifyResult.studentName}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-xs uppercase tracking-wider">Program Completed</span>
                      <span className="text-white font-medium">{verifyResult.courseName}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-xs uppercase tracking-wider">Certificate ID</span>
                      <span className="text-sky-400 font-mono">{verifyResult.certificateNo}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-xs uppercase tracking-wider">Status</span>
                      <span className="text-green-400 font-medium">Verified</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {verifyStatus === 'error' && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center gap-3 text-red-400"
            >
              <XCircle className="w-6 h-6 flex-shrink-0" />
              <p className="text-sm font-medium">{verifyError}</p>
            </motion.div>
          )}
        </motion.div>

        {/* Hero Section */}
        <motion.div 
          className="text-center max-w-4xl mx-auto pt-6 sm:pt-8 pb-8 sm:pb-10 relative"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Subtle glow behind hero text */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-[150%] bg-accent/10 blur-[100px] -z-10" />

          <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-[2.5rem] font-bold mb-5 tracking-tight text-white drop-shadow-lg leading-tight">
            Master Skills. Build Projects. <br />
            <span className="gradient-text-glow drop-shadow-[0_0_25px_rgba(167,139,250,0.4)]">Get Hired.</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-300 mb-8 max-w-2xl mx-auto leading-relaxed">
            Join our intensive Training cum Internship program. Learn from industry experts, work on live projects, and bridge the gap between classroom and corporate.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact" className="neon-btn neon-btn-primary shadow-[0_0_30px_rgba(167,139,250,0.5)]">
              Apply Now <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="#features" className="neon-btn neon-btn-outline bg-secondary/5 border-secondary/50 text-white">
              Explore Features
            </a>
          </div>
        </motion.div>

        {/* Features Section */}
        <div id="features" className="py-10 sm:py-12 border-t border-white/5 relative">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-4/5 h-[1px] bg-gradient-to-r from-transparent via-accent/30 to-transparent" />
          <div className="text-center mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">Why Choose Our Program?</h2>
            <p className="text-sm sm:text-base text-slate-400 max-w-2xl mx-auto leading-relaxed">Experience a premium learning environment designed to make you industry-ready.</p>
          </div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {features.map((feature, idx) => (
              <motion.div 
                key={idx} 
                variants={itemVariants}
                whileHover={{ y: -8, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                className="frosted-surface rounded-2xl p-8 group relative overflow-hidden cursor-pointer"
                style={{
                  background: "linear-gradient(145deg, rgba(10, 16, 41, 0.6), rgba(6, 11, 31, 0.9))",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
                }}
              >
                {/* Glowing border effect on hover */}
                <div className="absolute inset-0 border-[1.5px] border-sky-400/40 group-hover:border-sky-400/80 rounded-[15px] transition-colors duration-500 pointer-events-none" />
                
                {/* Background animated blob */}
                <div className="absolute -top-16 -right-16 w-40 h-40 bg-gradient-to-br from-sky-400/30 to-blue-500/30 rounded-full blur-3xl opacity-30 group-hover:opacity-100 transition-opacity duration-700" />
                
                <div className="relative z-10">
                  <motion.div 
                    className="w-14 h-14 rounded-2xl bg-slate-800 border border-sky-400/50 flex items-center justify-center mb-6 shadow-[0_0_20px_rgba(56,189,248,0.2)] group-hover:shadow-[0_0_35px_rgba(56,189,248,0.6)] transition-all duration-300 group-hover:bg-slate-700 text-sky-400"
                    whileHover={{ rotate: 10, scale: 1.15 }}
                  >
                    {feature.icon}
                  </motion.div>
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-sky-400 transition-colors duration-300">
                    {feature.title}
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed group-hover:text-slate-300 transition-colors duration-300">
                    {feature.desc}
                  </p>
                </div>

                {/* Bottom animated line */}
                <div className="absolute bottom-0 left-0 h-1 w-0 bg-gradient-to-r from-sky-400 to-blue-500 group-hover:w-full transition-all duration-500 ease-out" />
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* How It Works (Journey) Section */}
        <div className="py-10 sm:py-12">
          <div className="text-center mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">Your Journey to Success</h2>
            <p className="text-sm sm:text-base text-slate-400 max-w-2xl mx-auto leading-relaxed">A step-by-step path from a beginner to a highly employable professional.</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="relative border-l-2 border-sky-400/20 ml-6 md:ml-0 md:border-none">
              <div className="md:hidden absolute top-0 bottom-0 left-[-1px] w-0.5 bg-gradient-to-b from-sky-400 via-blue-500 to-transparent" />
              
              <div className="space-y-12">
                {journeySteps.map((step, idx) => (
                  <motion.div 
                    key={idx}
                    initial={{ opacity: 0, x: idx % 2 === 0 ? -50 : 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                    className={`relative flex flex-col md:flex-row items-start md:items-center gap-8 ${
                      idx % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                    }`}
                  >
                    {/* Desktop Connector Line */}
                    <div className="hidden md:block absolute top-1/2 left-1/2 w-full h-[2px] -translate-y-1/2 bg-gradient-to-r from-sky-400/20 via-sky-400/50 to-sky-400/20 -z-10 shadow-[0_0_10px_rgba(56,189,248,0.3)]" />
                    
                    {/* Content Box */}
                    <div className={`flex-1 w-full pl-8 md:pl-0 ${idx % 2 === 0 ? "md:pr-12" : "md:pl-12"} text-left`}>
                      <div className="frosted-surface rounded-2xl p-6 inline-block w-full gradient-border shadow-[0_8px_30px_rgba(0,0,0,0.3)] hover:shadow-[0_0_30px_rgba(56,189,248,0.2)] transition-shadow duration-300 border border-transparent hover:border-sky-400/40 text-left">
                        <div className="flex items-center gap-4 mb-3">
                          <div className="w-12 h-12 rounded-full bg-sky-400/10 flex items-center justify-center text-sky-400 font-extrabold border border-sky-400/50 shrink-0 shadow-[0_0_15px_rgba(56,189,248,0.3)]">
                            {step.step}
                          </div>
                          <h3 className="text-xl font-bold text-white group-hover:text-sky-400">{step.title}</h3>
                        </div>
                        <p className="text-slate-300 text-sm leading-relaxed">{step.desc}</p>
                      </div>
                    </div>

                    {/* Center Node (Desktop) */}
                    <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-16 h-16 rounded-full bg-[#060B1F] border-4 border-sky-400 items-center justify-center shadow-[0_0_35px_rgba(56,189,248,0.6)] z-10 text-sky-400">
                      {step.icon}
                    </div>

                    {/* Mobile Node */}
                    <div className="md:hidden absolute left-[-28px] top-4 w-14 h-14 rounded-full bg-[#060B1F] border-2 border-sky-400 flex items-center justify-center shadow-[0_0_20px_rgba(56,189,248,0.3)] z-10 text-sky-400">
                      {step.icon}
                    </div>

                    {/* Empty Space for Grid */}
                    <div className="hidden md:block flex-1" />
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Course Domains Section */}
        <div className="py-10 sm:py-12" id="domains">
          <div className="text-center mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">Explore Our Domains</h2>
            <p className="text-sm sm:text-base text-slate-400 max-w-2xl mx-auto leading-relaxed">Choose a domain that aligns with your career goals and dive deep into hands-on learning.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {displayPrograms.map((domain, idx) => (
              <motion.div 
                key={idx}
                whileHover={{ y: -5 }}
                className="frosted-surface rounded-2xl p-8 border border-sky-400/20 hover:border-sky-400/50 transition-all duration-300 relative group overflow-hidden flex flex-col h-full"
              >
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                  {domain.icon}
                </div>
                <div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-sky-400/10 text-sky-400 shadow-[0_0_15px_rgba(56,189,248,0.2)]">
                  {domain.icon}
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">{domain.title}</h3>
                <div className="inline-block px-3 py-1 rounded-full bg-slate-800 border border-slate-700 text-slate-300 text-xs font-semibold mb-4 self-start">
                  Duration: {domain.duration}
                </div>
                <div className="mb-6 flex-grow">
                  <span className="font-semibold text-slate-300 text-sm mb-3 block">Syllabus Highlights:</span>
                  <ul className="space-y-2">
                    {domain.modules.map((module, mIdx) => (
                      <li key={mIdx} className="flex items-start text-slate-400 text-sm">
                        <CheckCircle className="w-4 h-4 text-sky-400 mr-2 shrink-0 mt-0.5" />
                        <span>{module}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <button 
                  onClick={() => handleApplyClick(domain.title)}
                  className="mt-auto w-full py-3 rounded-xl bg-sky-400/10 hover:bg-sky-400/20 border border-sky-400/30 text-sky-400 font-semibold transition-all shadow-[0_0_10px_rgba(56,189,248,0.1)] hover:shadow-[0_0_20px_rgba(56,189,248,0.3)]"
                >
                  Apply for this
                </button>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Application Form Section */}
        <motion.div 
          id="apply-form"
          className="py-12 sm:py-14 max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="frosted-surface rounded-3xl p-8 md:p-12 relative overflow-hidden border border-sky-400/30 shadow-[0_10px_50px_rgba(0,0,0,0.5)]">
            <div className="absolute -top-32 -left-32 w-64 h-64 bg-sky-400/20 rounded-full blur-[100px]" />
            <div className="absolute -bottom-32 -right-32 w-64 h-64 bg-blue-500/20 rounded-full blur-[100px]" />
            
            <div className="relative z-10">
              <div className="text-center mb-10">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 text-white">Apply for Internship</h2>
                <p className="text-sm text-slate-400 leading-relaxed">Fill out the form below to secure your spot. We will review your application and get back to you.</p>
              </div>

              {submitMessage.text && (
                <div className={`p-4 rounded-xl mb-8 text-center border ${submitMessage.type === "success" ? "bg-green-500/10 border-green-500/50 text-green-400" : "bg-red-500/10 border-red-500/50 text-red-400"}`}>
                  {submitMessage.text}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-300">Full Name</label>
                    <input 
                      type="text" 
                      name="name" 
                      value={formData.name} 
                      onChange={handleInputChange}
                      required
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-400 transition-colors"
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-300">Email Address</label>
                    <input 
                      type="email" 
                      name="email" 
                      value={formData.email} 
                      onChange={handleInputChange}
                      required
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-400 transition-colors"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-300">Phone Number</label>
                    <input 
                      type="tel" 
                      name="phone" 
                      value={formData.phone} 
                      onChange={handleInputChange}
                      required
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-400 transition-colors"
                      placeholder="+91 9876543210"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-300">College / University</label>
                    <input 
                      type="text" 
                      name="college" 
                      value={formData.college} 
                      onChange={handleInputChange}
                      required
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-400 transition-colors"
                      placeholder="Your College Name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-300">Select Domain</label>
                  <select 
                    name="domain"
                    value={formData.domain}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-400 transition-colors appearance-none"
                  >
                    <option value="" disabled>Select a domain...</option>
                    {displayPrograms.map((d, i) => (
                      <option key={i} value={d.title}>{d.title}</option>
                    ))}
                    <option value="Other">Other / Not Sure</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-300">Why do you want to join this program?</label>
                  <textarea 
                    name="objective"
                    value={formData.objective}
                    onChange={handleInputChange}
                    required
                    rows="4"
                    className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-400 transition-colors resize-none"
                    placeholder="Briefly describe your goals..."
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full py-4 rounded-xl bg-sky-500 hover:bg-sky-400 text-slate-900 font-bold text-lg transition-colors flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(56,189,248,0.4)] hover:shadow-[0_0_30px_rgba(56,189,248,0.6)] disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <span className="animate-pulse">Submitting Application...</span>
                  ) : (
                    <>
                      Submit Application <Send className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </motion.div>

      </div>
      <FooterNew />
    </div>
  );
};


export default Internships;
